import { lazyLoad } from "unlazy";
import Viewer from "viewerjs";
import classNames from "classnames";
import { Component } from "inferno";

import { setIsoData } from "@utils/app";
import { IsoData } from "@utils/types";
import { getStaticDir } from "@utils/env";
import { masonryUpdate } from "@utils/browser";
import { ImageDetails } from "lemmy-js-client";
import { createRef } from "inferno";

const iconThumbnailSize = 96;
const thumbnailSize = 256;

// For some reason, masonry needs a default image size, and will properly size it down
const defaultImgSize = 512;
const bannerSize = 2048;

type PictrsImageType =
  | "large_thumbnail"
  | "thumbnail"
  | "icon"
  | "banner"
  | "icon_and_banner"
  | "icon_without_banner"
  | "card_top";

type Props = {
  src: string;
  type: PictrsImageType;
  alt?: string;
  nsfw?: boolean;
  imageDetails?: ImageDetails;
  viewer?: boolean;
};

type State = {
  src: string;
  viewerjs?: Viewer;
};

function handleImgLoadError(i: PictrsImage) {
  i.setState({
    src: `${getStaticDir()}/assets/images/broken-image-fallback.png`,
  });
}

export class PictrsImage extends Component<Props, State> {
  // TODO this should be a prop
  private readonly isoData: IsoData = setIsoData(this.context);
  private imageRef = createRef<HTMLImageElement>();
  private lazyLoadCleanup: undefined | (() => void);

  state: State = {
    src: this.props.src,
  };

  componentDidUpdate(prevProps: Props) {
    if (prevProps.src !== this.props.src) {
      this.setState({ src: this.props.src });
    }
  }

  componentDidMount() {
    if (this.imageRef.current) {
      this.lazyLoadCleanup = lazyLoad(this.imageRef.current);

      if (this.props.viewer) {
        const viewerjs = new Viewer(this.imageRef.current, {
          title: () => this.alt() ?? undefined,
          url: (image: { src: string }) => viewerJsFullSizeImageUrl(image),
          toolbar: false,
        });
        this.setState({ viewerjs });
      }
    }
  }

  componentWillUnmount() {
    this.lazyLoadCleanup?.();

    if (this.state.viewerjs) {
      this.state.viewerjs.destroy();
    }
  }

  render() {
    const { type, nsfw, imageDetails } = this.props;

    const blurImage =
      nsfw &&
      (this.isoData.myUserInfo?.local_user_view.local_user.blur_nsfw ??
        !this.isoData.siteRes.site_view.site.content_warning);

    const [width, height] = this.widthAndHeight();

    // Unlazy recommends you manually set the src to the blurred image.
    // https://unlazy.byjohann.dev/guide/usage.html
    //
    // A testable blurhash
    // const blurhash = "LEHV6nWB2yk8pyo0adR*.7kCMdnj";

    return (
      !this.isoData.showAdultConsentModal && (
        <picture>
          <img
            ref={this.imageRef}
            src={base64Placeholder(width, height)}
            data-src={buildPictrsSrc(this.state.src, type)}
            data-blurhash={imageDetails?.blurhash}
            alt={this.alt()}
            title={this.alt()}
            loading="lazy"
            width={width}
            height={height}
            className={classNames("overflow-hidden pictrs-image", {
              "img-fluid":
                type !== "icon" &&
                type !== "icon_and_banner" &&
                type !== "icon_without_banner",
              "thumbnail rounded object-fit-cover": type === "thumbnail",
              "img-expanded slight-radius":
                type !== "thumbnail" && type !== "icon",
              "img-blur": type === "thumbnail" && nsfw,
              "object-fit-cover img-icon me-1": type === "icon",
              "img-blur-icon": type === "icon" && blurImage,
              "img-blur-thumb": type === "thumbnail" && blurImage,
              "avatar rounded-circle object-fit-cover":
                type === "icon_and_banner" || type === "icon_without_banner",
              // The icon and banner uses an overlay
              "avatar-overlay": type === "icon_and_banner",
              "card-img-top": type === "card_top",
            })}
            onLoad={() => masonryUpdate()}
            onError={() => handleImgLoadError(this)}
          />
        </picture>
      )
    );
  }

  alt(): string {
    switch (this.props.type) {
      case "icon":
      case "banner":
      case "icon_and_banner":
        return "";
      default:
        return this.props.alt || "";
    }
  }

  widthAndHeight(): [number, number] {
    switch (this.props.type) {
      case "icon":
        return [iconThumbnailSize, iconThumbnailSize];
      case "thumbnail":
        return [thumbnailSize, thumbnailSize];
      case "banner":
        return [bannerSize, bannerSize];
      default:
        return [
          this.props.imageDetails?.width ?? defaultImgSize,
          this.props.imageDetails?.height ?? defaultImgSize,
        ];
    }
  }
}

/**
 * If an image has a pictrs-like URL, it will fetch an appropriately sized image.
 */
export function buildPictrsSrc(src: string, type: PictrsImageType): string {
  // sample pictrs url:
  // http://localhost:16102/api/v4/image/file.webp?max_size=256

  let url: URL | undefined;
  try {
    url = new URL(src);
  } catch {
    return src;
  }

  // If there's no match, then it's not a pictrs image
  if (
    !url.pathname.includes("/api/v3/image") &&
    !url.pathname.includes("/api/v4/image")
  ) {
    return src;
  }

  switch (type) {
    case "thumbnail":
      url.searchParams.set("max_size", thumbnailSize.toString());
      break;
    case "icon":
    case "icon_without_banner":
      url.searchParams.set("max_size", iconThumbnailSize.toString());
      break;
    case "banner":
    case "icon_and_banner":
    case "card_top":
      url.searchParams.set("max_size", bannerSize.toString());
      break;
    case "large_thumbnail":
      // Use bannerSize here for slight downscaling, but larger than thumbnail
      url.searchParams.set("max_size", bannerSize.toString());
      break;
    default:
      url.searchParams.set("max_size", defaultImgSize.toString());
      break;
  }

  return url.href;
}

function base64Placeholder(width: number = 32, height: number = 32) {
  return `data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${width} ${height}'%3e%3c/svg%3e`;
}

export function viewerJsFullSizeImageUrl(image: { src: string }): string {
  // Remove the max_size params from the image viewer
  const srcUrl = new URL(image.src);
  srcUrl.searchParams.delete("max_size");

  return srcUrl.href;
}
